import {
  GetProductById,
  GetVariantSalePriceById,
  GetProductsByBatch,
  GetProductsByCollectionId,
  GetCollectionById,
  GetCollectionsByBatch,
  GetCollectionsByTitles,
  SearchProducts,
  GetUpsellProductsById,
} from 'lib-queries';

// Get current locale from URL path
function getCurrentLocale() {
  const path = window.location.pathname;

  const match = path.match(/^\/(fr|es|nl|de)(?:\/|$)/);
  return match ? match[1] : 'en';
}


/**
 * A simple client for working with the Storefront GraphQL API.
 */
class StorefrontClient {
  /**
   * @param {string} domain - The domain of the Shopify store.
   * @param {string} accessToken - The access token for the Storefront API.
   * @param {string} apiVersion - The API version to use.
   */
  constructor(
    domain = __NOVA_GLOBAL__.domain,
    accessToken = __NOVA_GLOBAL__.storefrontToken,
    apiVersion = '2023-01'
  ) {
    this.endpoint = `https://${domain}/api/${apiVersion}/graphql.json`;
    this.headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': accessToken,
    };
  }

  /**
   * Execute a GraphQL query against the endpoint.
   * @param {string} graphQLQuery - The GraphQL query to be executed.
   * @param {Object} variables - The variables for the query.
   * @returns {Promise<Object>} The JSON response from the endpoint.
   */
  async query(graphQLQuery, variables = {}) {
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({
          query: graphQLQuery,
          variables,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Shopify GraphQL Error: ${JSON.stringify(errorData)}`);
      }

      return await response.json();
    } catch (error) {
      console.error('An error occurred while querying Shopify:', error);
      throw error;
    }
  }

  /**
   * Fetch product data by its ID.
   * @param {string} id - The product ID.
   * @returns {Promise<Object>} The processed product data.
   */
  async getProductById(id) {
    const response = await this.query(GetProductById, {
      id: `gid://shopify/Product/${id}`,
    });
    return cleanUpProductData(response.data);
  }

  /**
   * Fetch Upsell products data by a product's ID.
   * @param {string} id - The product ID.
   * @param {string} countryCode - The country code for the currency.
   * @returns {Promise<Object>} The processed product data.
   */
  async getUpsellProductsById(id, countryCode = 'US') {
    const response = await this.query(GetUpsellProductsById(countryCode), {
      id: `gid://shopify/Product/${id}`,
    });
    return cleanUpProductData(response.data);
  }

  /**
   * Fetch variant sale price by its ID.
   * @param {string} id - The variant ID.
   * @param {string} countryCode - The country code for the currency.
   * @returns {Promise<Object>} The processed variant sale price.
   */
  async getVariantSalePriceById(id, countryCode = 'US') {
    const response = await this.query(GetVariantSalePriceById(countryCode), {
      id: `gid://shopify/ProductVariant/${id}`,
    });
    return response.data.node;
  }

  /**
   * Fetch batch of products data by their IDs.
   * @param {string} ids - The product IDs.
   * @returns {Promise<Object>} The processed products data.
   */
  async getProductsByBatch(ids) {
    const response = await this.query(GetProductsByBatch, {
      ids: ids.map((id) => `gid://shopify/Product/${id}`),
    });
    return response.data.nodes
      .filter(Boolean)
      .map((product) => cleanUpProductData({ node: product }));
  }

  /**
   * Fetch collection data by its ID.
   * @param {string} id - The collection ID.
   * @param {number} limit - The max number of products in the collection to fetch
   * @returns {Promise<Object>} The processed collection data.
   */
  async getCollectionById(id, limit) {
    const response = await this.query(GetCollectionById, {
      id: `gid://shopify/Collection/${id}`,
      limit,
    });
    return cleanUpCollectionData(response.data);
  }

  /**
   * Fetch products only based on collection ID.
   * @param {string} id - The collection ID.
   * @param {number} limit - The max number of products in the collection to fetch.
   * @returns {Promise<Array>} All products for the collection.
   */
  async getProductsByCollectionId(id, limit) {
    const allProducts = [];
    const maxRequests = 10;
    let requestCount = 0;
    const fetchPage = async (id, limit, cursor) => {
      if (requestCount >= maxRequests) {
        return;
      }
      const params = {
        id: `gid://shopify/Collection/${id}`,
        limit,
      };
      if (cursor) params.afterCursor = cursor;
      const response = await this.query(GetProductsByCollectionId, params);
      const products = cleanUpCollectionData(response.data).products;
      allProducts.push(...products);
      const pageInfo = response.data.node.products.pageInfo;
      if (pageInfo.hasNextPage) {
        requestCount++;
        await fetchPage(id, limit, pageInfo.endCursor);
      }
    };
    await fetchPage(id, limit);
    return allProducts;
  }

  /**
   * Fetch batch of collection data by their IDs.
   * @param {string} ids - An array of the collection IDs.
   * @returns {Promise<Object>} The processed collections data.
   */
  async getCollectionsByBatch(ids) {
    const response = await this.query(GetCollectionsByBatch, {
      ids: ids.map((id) => `gid://shopify/Collection/${id}`),
    });
    return response.data.nodes
      .filter(Boolean)
      .map((collection) => cleanUpCollectionData({ node: collection }));
  }

  /**
   * Fetch collections based on an array of titles.
   *
   * @function GetCollectionsByTitles
   * @param {string[]} titles - An array of collection titles to search for.
   * @returns {object[]} An array of collections matching the provided titles.
   */
  async getCollectionsByTitles(titles) {
    const titlesQuery = titles.map((title) => `title:${title}`).join(' OR ');
    const response = await this.query(GetCollectionsByTitles, {
      titlesQuery,
    });
    return response.data.collections.edges
      .filter(Boolean)
      .map((collection) => cleanUpCollectionData(collection));
  }

  /**
   * Search products.
   * @param {string} query - The search query.
   * @returns {Promise<Array<Object>>} The list of matched products.
   */
  async searchProducts(query, limit = 250) {
    const response = await this.query(SearchProducts, { query, limit });
    return cleanUpSearchData(response.data);
  }
}

/**
 * Fetches the product recommendations for a given product.
 *
 * @param {string} productId - The product ID.
 * @param {number} limit - The max number of products to fetch
 * @param {string} intent - The recommendation intent.
 * @returns {Promise<Object>} The processed product data.
 */
const getProductRecommendations = async (
  productId,
  limit = 10,
  intent = 'related'
) => {
  const response = await fetch(
    `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`
  );
  const { products } = await response.json();
  return products;
};

/**
 * Strips GraphQL Global ID (GID) down to its numeric component.
 * @param {string} value - The GID.
 * @returns {string} The stripped GID.
 */
function stripGID(value) {
  const gidPattern = /^gid:\/\/shopify\/\w+\//;
  if (gidPattern.test(value)) {
    return value.replace(gidPattern, '');
  }
  return value;
}

/**
 * Makes metafield data from GraphQL workable with JS.
 * @param {Array<Object>} rawMetafields - The GraphQL response for metafields.
 * @returns {Object} The processed metafield data.
 */
function cleanUpMetafields(rawMetafields) {
  if (!rawMetafields) return {};
  return rawMetafields
    .filter((m) => m !== null)
    .reduce((acc, metafield) => {
      if (!acc[metafield.namespace]) {
        acc[metafield.namespace] = {};
      }
      let value;
      if (metafield.references) {
        value = metafield.references.edges.map((edge) => {
          return cleanUpProductData({ node: edge.node });
        });
      } else if (Object.keys(metafield.reference || {}).includes('image')) {
        value = metafield.reference.image.originalSrc;
      } else if (Object.keys(metafield.reference || {}).includes('url')) {
        value = metafield.reference.url;
      } else if (Object.keys(metafield.reference || {}).includes('fields')) {
        metafield.reference.fields.forEach((field) => {
          if (field.reference != null) {
            value = field.reference;
          }
        });
      } else if (isStringifiedArray(metafield.value)) {
        const array = JSON.parse(metafield.value);
        value = array.map((i) => stripGID(i));
      } else {
        value = stripGID(metafield.value);
      }
      acc[metafield.namespace][metafield.key] = value;
      return acc;
    }, {});
}

/**
 * Makes product data from GraphQL workable with JS.
 * @param {Object} rawData - The GraphQL response for product.
 * @returns {Object|null} The processed product data.
 */
function cleanUpProductData(rawData) {
  if (!rawData || !rawData.node) {
    return null;
  }

  const rawProduct = rawData.node;

  const variantsSortedByPrice =
    rawProduct.variants?.edges.sort((a, b) => {
      const priceA = parseFloat(a.node.priceV2.amount);
      const priceB = parseFloat(a.node.priceV2.amount);
      if (priceA < priceB) return -1;
      if (priceA > priceB) return 1;
      return 0;
    }) || [];
  const lowestPriceVariant = variantsSortedByPrice[0];
  let compareAtPrice = null;
  if (variantsSortedByPrice.length) {
    const compareAtPriceProperty =
      lowestPriceVariant.node.compareAtPriceV2?.amount;
    if (compareAtPriceProperty)
      compareAtPrice = parseFloat(compareAtPriceProperty);
  }

  return {
    id: stripGID(rawProduct.id),
    available: rawProduct.availableForSale,
    title: rawProduct.title,
    description: rawProduct.descriptionHtml,
    minPrice: parseFloat(rawProduct.priceRange?.minVariantPrice?.amount),
    maxPrice: parseFloat(rawProduct.priceRange?.maxVariantPrice?.amount),
    price: parseFloat(rawProduct.priceRange?.minVariantPrice?.amount),
    compareAtPrice,
    compare_at_price: compareAtPrice,
    handle: rawProduct.handle,
    type: rawProduct.productType,
    vendor: rawProduct.vendor,
    createdAt: new Date(rawProduct.createdAt),
    updatedAt: new Date(rawProduct.updatedAt),
    publishedAt: new Date(rawProduct.publishedAt),
    onlineStoreUrl: rawProduct.onlineStoreUrl,
    tags: rawProduct.tags,
    featured_image: rawProduct.featuredImage?.src,
    images: rawProduct.images?.edges.map((edge) => edge.node),
    options: rawProduct.options,
    collections: rawProduct.collections?.edges.map((edge) => {
      return cleanUpCollectionData(edge);
    }),
    variants: rawProduct.variants?.edges.map((edge) => {
      return {
        id: stripGID(edge.node.id),
        title: edge.node.title,
        price: parseFloat(edge.node.priceV2.amount),
        compareAtPrice: parseFloat(edge.node.compareAtPriceV2?.amount),
        compare_at_price: parseFloat(edge.node.compareAtPriceV2?.amount),
        currencyCode: edge.node.priceV2.currencyCode,
        availableForSale: edge.node.availableForSale,
        available: edge.node.availableForSale,
        sku: edge.node.sku,
        selectedOptions: edge.node.selectedOptions,
        options: edge.node.selectedOptions.map((option) => {
          return option.value;
        }),
        option1: edge.node.selectedOptions[0]?.value,
        option2: edge.node.selectedOptions[1]?.value,
        quantityAvailable: edge.node.quantityAvailable,
        metafields: cleanUpMetafields(edge.node.metafields),
      };
    }),
    metafields: cleanUpMetafields(rawProduct.metafields),
    isDiscounted: rawProduct.variants?.edges.some((variantEdge) => {
      const variant = variantEdge.node;
      const originalPrice = parseFloat(variant.priceV2.amount);
      const compareAtPrice = parseFloat(variant.compareAtPriceV2?.amount);
      return compareAtPrice > originalPrice;
    }),
  };
}

/**
 * Process the search data response.
 * @param {Object} rawData - The raw search data.
 * @returns {Array<Object>} The list of processed product data.
 */
function cleanUpSearchData(rawData) {
  if (!rawData) {
    return null;
  }
  return rawData.products.edges.map((product) => cleanUpProductData(product));
}

/**
 * Makes collection data from GraphQL workable with JS.
 * @param {Object} rawData - The GraphQL response for collection.
 * @returns {Object|null} The processed collection data.
 */
function cleanUpCollectionData(rawData) {
  if (!rawData || !rawData.node) {
    return null;
  }

  const rawCollection = rawData.node;

  return {
    id: stripGID(rawCollection.id),
    title: rawCollection.title,
    handle: rawCollection.handle,
    description: rawCollection.descriptionHtml,
    updatedAt: new Date(rawCollection.updatedAt),
    publishedAt: new Date(rawCollection.publishedAt),
    image: rawCollection.image,
    products: rawCollection.products?.edges.map((edge) => {
      return cleanUpProductData(edge);
    }),
    metafields: cleanUpMetafields(rawCollection.metafields),
  };
}

/**
 * Checks whether a value represents a valid number.
 * @example
 * validateNumber(NaN)       // returns false
 * validateNumber(undefined) // returns false
 * validateNumber(1)         // returns true
 * validateNumber(1.1)       // returns true
 * validateNumber('1')       // returns true
 * validateNumber('1.1')     // returns true
 * validateNumber('$1.00')   // returns false (preformatted price needs a different check)
 *
 * @param {*} value - The value to check.
 * @returns {boolean} - True if value is a valid number, false otherwise.
 */
const validateNumber = (value) => Number.isFinite(parseFloat(value));

/**
 * Converts number representing a price in minor unit (e.g. cents) to major unit (e.g. dollars).
 *
 * @param {number|string} priceInMinorUnit - The price in minor unit.
 * @returns {number|string|false} - The price in major unit or false.
 */
function minorToMajorUnit(priceInMinorUnit) {
  if (!validateNumber(priceInMinorUnit)) return false;
  return (priceInMinorUnit / 100).toFixed(2);
}

/**
 * Simple formatter to convert price to string.
 *
 * @param {number|string} priceInMinorUnit - The price in minor unit.
 * @param {string} [currencyUnit='$'] - The currency symbol.
 * @returns {string} - Formatted price string.
 */
function formatPrice(priceInMinorUnit, currencyUnit = '$') {
  const priceInMajorUnit = minorToMajorUnit(priceInMinorUnit);
  if (!priceInMajorUnit) return '';
  return `${currencyUnit}${priceInMajorUnit}`;
}

/**
 * Converts string in snake case to camel case.
 *
 * @param {string} string - String to convert.
 * @returns {string} - Converted string in camel case.
 */
function snakeToCamel(string) {
  return string.replace(/(_\w)/g, (matches) => matches[1].toUpperCase());
}

/**
 * Simple debounce function that prevents excessive function calls.
 *
 * @param {Function} func - Function to debounce.
 * @param {number} delay - Interval within which call cannot be re-executed.
 * @returns {Function} - Debounced function.
 */
function debounce(func, delay) {
  let timer;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
}

/**
 * Formats a number representing cents into a money string based on the provided format.
 *
 * @function
 * @param {(string | number)} cents - The amount of money in cents. If a string is provided, it will attempt to clean it.
 * @param {string} [format] - The format to output the money string in. If not provided, it will use the global money format.
 * @returns {string} - The formatted money string.
 * @example
 * // Returns "$19.99"
 * formatMoney(1999, "{{amount}}");
 * @example
 * // Returns "19.99"
 * formatMoney(1999, "{{amount_no_decimals}}");
 */
function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const defaultTo = function (value, defaultValue) {
    return value == null || value !== value ? defaultValue : value;
  };

  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || window.__NOVA_GLOBAL__.moneyFormat;

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ',');
    decimal = defaultTo(decimal, '.');

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      '$1' + thousands
    );
    const centsAmount = decimal + parts[1];

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  // return formatString.replace(placeholderRegex, value).replace('.00', '')
  return formatString.replace(placeholderRegex, value);
}

/**
 * Checks if a given string represents a valid stringified array.
 *
 * @param {string} string - The string to check.
 * @returns {boolean} - Returns true if the string is a valid stringified array, otherwise false.
 */
function isStringifiedArray(string) {
  if (typeof string !== 'string') return false;
  if (string.startsWith('[') && string.endsWith(']')) {
    try {
      const potentialArray = JSON.parse(string);
      return Array.isArray(potentialArray);
    } catch (error) {
      return false;
    }
  }
  return false;
}
/**
 * Adds one or more product variants to the Shopify cart,
 * but if a variant is already in the cart, increments its quantity
 * instead of creating a new line item.
 *
 * @async
 * @param {Array|Object} inputItems - An array of { id, quantity } or a single one.
 * @param {boolean} [openCart=true] - Whether to open the cart after operation.
 */
const addToCart = async function (inputItems, openCart = true) {
  // Normalize to an array
  const items = Array.isArray(inputItems) ? inputItems : [inputItems];

  // 1) Fetch current cart so we know what's already in there
  const cartResponse = await fetch('/cart.js', { method: 'GET' });
  if (!cartResponse.ok) {
    console.error('Could not fetch cart');
    // Fallback: just try adding everything
  }
  const cart = await cartResponse.json().catch(() => ({ items: [] }));

  // 2) Separate new vs. existing
  const toAdd = [];
  const toUpdate = [];

  items.forEach(({ id, quantity, properties }) => {
    // Shopify's cart.js returns items with variant_id and a unique line-item key.
    // Skip bundle line items — they have _bundle_key in their properties and
    // should never be merged with non-bundle additions.
    const existing = cart.items.find(
      (ci) =>
        String(ci.variant_id) === String(id) &&
        !(ci.properties && ci.properties._bundle_key)
    );
    if (existing) {
      // schedule an update of that line-item's quantity
      toUpdate.push({
        id: existing.key,
        quantity: existing.quantity + quantity,
      });
    } else {
      const newItem = { id, quantity };

      // Add properties if they exist
      if (properties) {
        newItem.properties = properties;
      }

      toAdd.push(newItem);
    }
  });

  // 3) Fire off /cart/add.js for truly new variants
  if (toAdd.length) {
    const resp = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: toAdd }),
    });
    if (!resp.ok) {
      console.error('Error adding items:', await resp.text());
      throw new Error('addToCart add.js failed');
    }
  }

  // 4) Fire off /cart/update.js for increments
  if (toUpdate.length) {
    await updateCartQuantity(toUpdate, false);
  }

  // 5) Optionally open & refresh cart
  if (openCart) {
    document.dispatchEvent(
      new CustomEvent('nova:cart:refresh', { bubbles: true, composed: true })
    );
    document.dispatchEvent(
      new CustomEvent('nova:cart:open', { bubbles: true, composed: true })
    );
  }
};
/**
 * Update the cart's line item quantities
 *
 * @async
 * @function
 * @param {Array|Object} items - An array of objects or a single object representing the items to update
 * Each object should have an `id` property (variant ID or line item key) and a `quantity` property.
 * @param {boolean} [openCart=true] Whether to trigger the cart to open after updating.
 * @throws Will throw an error if the fetch operation encounters a problem.
 * @example
 * updateCartQuantity({ id: '1234567890', quantity: 1 });
 * updateCartQuantity([{ id: '1234567890', quantity: 1 }, { id: '0987654321', quantity: 2 }]);
 */
let abortUpdateCartQuantityController = null;

async function updateCartQuantity(inputItems, openCart = true) {
  const items = Array.isArray(inputItems) ? inputItems : [inputItems];

  if (abortUpdateCartQuantityController) {
    abortUpdateCartQuantityController.abort();
  }
  abortUpdateCartQuantityController = new AbortController();

  try {
    const updates = {};
    for (const item of items) {
      updates[item.id] = item.quantity;
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
      signal: abortUpdateCartQuantityController.signal,
    });

    if (!response.ok) {
      throw new Error('Error updating quantity in cart');
    }

    if (openCart) {
      document.dispatchEvent(
        new CustomEvent('nova:cart:open', { bubbles: true, composed: true })
      );
    }
    document.dispatchEvent(
      new CustomEvent('nova:cart:refresh', {
        bubbles: true,
        composed: true,
      })
    );

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error(
        'There was a problem with the fetch operation:',
        error.message
      );
    }
  }
}

/**
 * Interpolates a template string with provided replacements.
 *
 * @param {string} template - The string template with placeholders.
 * @param {Object} replacements - An object containing key-value pairs for placeholders and their replacements.
 * @returns {string} The interpolated string.
 *
 * @example
 *   const messageTemplate = 'Hello, {name}! You have {count} messages.';
 *   const values = {
 *     name: 'Alice',
 *     count: 5
 *   }
 *   const message = interpolate(messageTemplate, values);
 *   console.log(message); // "Hello, Alice! You have 5 messages."
 */
function interpolate(template, replacements) {
  return template.replace(/\{(\w+)\}/g, (_, placeholder) => {
    return replacements[placeholder] || _;
  });
}

/**
 * Finds and returns a slotted child within a Web Component
 *
 * @param {DOMElement} shadowRoot
 * @param {String} name
 * @param {Boolean} firstOnly
 * @returns DOMElement | Empty
 */
function getChildSlot(shadowRoot = null, name = '', firstOnly = false) {
  const children =
    shadowRoot
      ?.querySelector('slot')
      ?.assignedElements({ flatten: true })
      .filter((node) => {
        return node.matches(name);
      }) || [];
  return firstOnly ? (children || [])[0] : children;
}

/**
 * A shorthand for querySelector
 *
 * @param {String} selector
 * @param {DomElement} parent
 * @returns DOMElement | Empty
 */
function one(selector, parent) {
  parent || (parent = document);
  return parent.querySelector(selector);
}

/**
 * A shorthand for querySelectorAll
 *
 * @param {String} selector
 * @param {DomElement} parent
 * @returns DOMElement | Empty
 */
function all(selector, parent) {
  parent || (parent = document);
  var selection = parent.querySelectorAll(selector);
  return Array.prototype.slice.call(selection);
}

/**
 * Toggles an array of classes on a DOMElement
 *
 * @param {DOMElement} element
 * @param {Array} classes
 */
function toggle(element, classes = []) {
  classes.forEach((_class) => {
    element.classList.toggle(_class);
  });
}

/**
 * Not lovely & not pure; attempts to pause all currently
 * playing media (ie. videos)
 */
const pauseAllMedia = () => {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*'
    );
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document
    .querySelectorAll('product-model')
    .forEach((model) => model.modelViewerUI?.pause());
};

/**
 * A classic throttle helper
 *
 * @param {Function} callback
 * @param {Number} limit
 * @returns
 */
const throttle = (callback, limit) => {
  let waiting = false;
  return function () {
    if (!waiting) {
      callback.apply(this, arguments);
      waiting = true;
      setTimeout(function () {
        waiting = false;
      }, limit);
    }
  };
};

/**
 * Serializes form
 *
 * @param {Form} form
 * @returns String
 */
const serializeForm = (form) => {
  const obj = {};
  const formData = new FormData(form);

  for (const key of formData.keys()) {
    // ensures that the line item properties are added as an object, instead of as an array
    const regex = /(?:^(properties\[))(.*?)(?:\]$)/;

    if (regex.test(key)) {
      obj.properties = obj.properties || {};
      obj.properties[regex.exec(key)[2]] = formData.get(key);
    } else {
      obj[key] = formData.get(key);
    }
  }

  return JSON.stringify(obj);
};

/**
 * Returns a generic fetch config object (XHR)
 *
 * @param {String} type
 * @param {String} method
 * @returns Object
 */
const fetchConfig = (type = 'json', method = 'POST') => {
  return {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
};

/**
 * Checks to see if the device supports touch gestures.
 */
const isTouch = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints;
};

/**
 * Clones a complex Javascript object. This is a
 * thorough way to ensure that the object is fully
 * cloned with no references kept in place.
 *
 * @param {Object} obj
 */
const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Removes whitespace within a string and makes all
 * characters lowercase.
 *
 * @param {String} str
 */
const handleize = (str) => str.replace(/[ /_]/g, '-').toLowerCase();

/**
 * Decodes a string that has been encoded through the 'url_encode' Shopify filter
 * @param {*} str
 */
const decode = (str) => decodeURIComponent(str).replace(/\+/g, ' ');

/**
 * Returns an iFrame src URL value for either YouTube or Vimeo hosted videos
 *
 * @param {string} host 'youtube' or 'vimeo'
 * @param {string} id the external video ID
 * @param {boolean} autoplay whether the video should autoplay
 * @param {boolean} controls whether the video should show controls
 * @param {boolean} loop whether the video should loop
 * @param {boolean} sound whether the video should have sound
 */
const externalVideoEmbedSrc = (
  host = 'youtube',
  id,
  autoplay = false,
  controls = false,
  loop = false,
  sound = false
) => {
  let src = '';
  const queryParam = (param, value) => {
    return `${param}=${Number(value)}`;
  };
  const autoplayParam = queryParam('autoplay', autoplay);
  const controlsParam = queryParam('controls', controls);
  const loopParam = queryParam('loop', loop);
  const queryParams = `${autoplayParam}&${controlsParam}&${loopParam}`;
  let muted;

  switch (host) {
    case 'youtube':
      muted = sound ? `` : `mute=1&`;
      src = `https://www.youtube.com/embed/${id}?${queryParams}&enablejsapi=1&modestbranding=1&${muted}cc_load_policy=0&disablekb=0&fs=1&iv_load_policy=1&playsinline=1&rel=1&widgetid=1`;
      break;
    case 'vimeo':
      muted = sound ? `` : `muted=1&`;
      src = `https://player.vimeo.com/video/${id}?${queryParams}&${muted}byline=0&playsinline=1&title=0`;
      break;
    default:
      console.error('Invalid host value (must be "youtube" or "vimeo")');
  }
  return src;
};

/**
 * Gets the previous sibling of an element that matches a given selector
 *
 * @param {HTMLElement} element the element to start from
 * @param {string} selector the selector to match
 */
const getPreviousSibling = (element, selector) => {
  let sibling = element.previousElementSibling;

  if (!selector) return null;

  while (sibling) {
    if (sibling.matches(selector)) {
      return sibling;
    }
    sibling = sibling.previousElementSibling;
  }
};

const t = (namespace, fallback) => {
  return window.langMessages[namespace]
    ? window.langMessages[namespace]
    : fallback;
};

/**
 * Determines the current viewport category based on the window's inner width.
 *
 * @function
 * @returns {string} - The current viewport category, which can be 'mobile', 'tablet', or 'desktop'.
 * @example
 * const viewport = getViewport();
 * console.log(viewport); // Outputs: 'mobile', 'tablet', or 'desktop'
 */
function getViewport() {
  const width = window.innerWidth;
  if (width <= 400) {
    return 'small-mobile';
  } else if (width > 400 && width <= 480) {
    return 'mobile';
  } else if (width > 480 && width <= 768) {
    return 'tablet';
  } else if (width > 768 && width <= 990) {
    return 'large-tablet';
  } else if (width > 990 && width <= 1440) {
    return 'desktop';
  } else {
    return 'large-desktop';
  }
}

/**
 * Query the Stamped rating badge based on the product IDs.
 *
 * @function
 * @returns {data} - The row data containing the Stamped rating badge.
 */

async function getStampedRatingData(products) {
  const url =
    'https://stamped.io/api/widget/badges?isIncludeBreakdown=true&isIncludehtml=true';

  if (products.length === 0) return;

  const productIds = products.map((product) => ({
    productId: product.id,
  }));

  const body = {
    productIds: productIds,
    apiKey: window.stampedAuth.apiKey,
    storeUrl: window.stampedAuth.sId,
  };

  const headers = {
    'Content-Type': 'application/json',
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(body),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error:', error);
  }
}

/**
 * Render the Stamped rating badge for a product in shadow DOMs.
 *
 * @function
 * @returns {ratingContainer} - The element containing the Stamped rating badge.
 */
function renderStampedRatingBadge(ratingData, productId) {
  // Added check to ensure ratingData is an array
  if (!Array.isArray(ratingData)) {
    // console.error("renderStampedRatingBadge: ratingData is not an array:", ratingData)
    return;
  }
  if (ratingData.length === 0) return;
  const matchedRatingData = ratingData.find(
    (el) => Number(el.productId) === Number(productId)
  );

  if (!matchedRatingData || Object.keys(matchedRatingData).length === 0) return;

  const ratingContainer = document.createElement('div');
  ratingContainer.innerHTML = matchedRatingData.badge;

  const ratingReview = ratingContainer.querySelector('.stamped-badge-caption');
  if (ratingReview && Number(ratingReview.dataset.reviews) > 0) {
    ratingReview.querySelector('span').removeAttribute('style');
  }

  return ratingContainer;
}

/**
 * Trigger custom event 'focus-trap:refresh'.
 */

function refreshFocusTrap(element, timeout = 0) {
  setTimeout(() => {
    element.dispatchEvent(
      new CustomEvent('focus-trap:refresh', { bubbles: true, composed: true })
    );
  }, timeout);
}
function readMoreToggle() {
  document.body.addEventListener('click', (event) => {
    if (!event.target.matches('[data-toggle]')) return;
    const showLess = event.target.dataset.less;
    event.preventDefault();

    const parent = event.target.closest('.desc-container');
    if (!parent) return;

    const descriptionShort = parent.querySelector('[data-description-short]');
    const descriptionFull = parent.querySelector('[data-description-full]');

    if (event.target.dataset.toggle === 'expand') {
      descriptionShort.classList.add('hidden');
      descriptionFull.classList.remove('hidden');

      let targetP = descriptionFull.querySelector('p:last-of-type');
      if (!targetP) {
        targetP = descriptionFull;
      }

      if (
        targetP &&
        !targetP.querySelector("a.read-btn[data-toggle='collapse']")
      ) {
        const link = document.createElement('a');
        link.className = 'read-btn';
        link.dataset.toggle = 'collapse';
        link.href = '#';
        link.textContent = showLess;
        targetP.appendChild(document.createTextNode(' '));
        targetP.appendChild(link);
      }
    } else {
      descriptionShort.classList.remove('hidden');
      descriptionFull.classList.add('hidden');
    }
  });
}

async function updateCartIconBadge() {
  const sectionResponse = await fetch('/cart?section_id=header');
  const html = await sectionResponse.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const newCartButton = doc.querySelector(
    'div.header__menu.header__utility > sl-button.header-button__small.header-button__small-cart.unstyled.js-cart-toggle'
  );
  const oldCartButton = document.querySelector(
    'div.header__menu.header__utility > sl-button.header-button__small.header-button__small-cart.unstyled.js-cart-toggle'
  );

  if (!newCartButton || !oldCartButton) {
    console.warn('Cart button element not found');
    return;
  }

  oldCartButton.replaceWith(newCartButton);

  //Reattach event listener
  const $openCart = document.querySelector('.js-cart-toggle');

  $openCart.addEventListener('click', () => {
    document.dispatchEvent(new CustomEvent('nova:cart:open'));
  });
}

// Horizontal pointer scroll for upsell items
function upsellCartScroll() {
  document.querySelectorAll('.cart-upsell-items').forEach((elem) => {
    elem.addEventListener(
      'wheel',
      (e) => {
        const canScroll = elem.scrollWidth > elem.clientWidth;
        if (!canScroll) return;

        const maxScroll = elem.scrollWidth - elem.clientWidth;
        const goingUp = e.deltaY < 0;
        const goingDown = e.deltaY > 0;

        const shouldIntercept =
          (goingUp && elem.scrollLeft > 0) ||
          (goingDown && elem.scrollLeft < maxScroll);

        if (shouldIntercept) {
          e.preventDefault();
          elem.scrollLeft += e.deltaY;
        }
      },
      { passive: false }
    );
  });
}

export {
  StorefrontClient,
  getProductRecommendations,
  cleanUpProductData,
  cleanUpCollectionData,
  validateNumber,
  minorToMajorUnit,
  formatPrice,
  formatMoney,
  snakeToCamel,
  debounce,
  addToCart,
  updateCartQuantity,
  interpolate,
  getChildSlot,
  one,
  all,
  toggle,
  pauseAllMedia,
  throttle,
  serializeForm,
  fetchConfig,
  isTouch,
  deepClone,
  handleize,
  decode,
  externalVideoEmbedSrc,
  getPreviousSibling,
  t,
  getViewport,
  getStampedRatingData,
  renderStampedRatingBadge,
  refreshFocusTrap,
  readMoreToggle,
  updateCartIconBadge,
  upsellCartScroll,
  getCurrentLocale,
};
